"use client";

import { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollSmoother, ScrollTrigger } from "gsap/all";

gsap.registerPlugin(ScrollTrigger, ScrollSmoother);

interface SmoothScrollLayoutProps {
  children: React.ReactNode;
  wrapperClassName?: string;
  contentClassName?: string;
  smooth?: number;
  effects?: boolean;
}

const SmoothScrollLayout = ({
  children,
  wrapperClassName = "",
  contentClassName = "",
  smooth = 3,
  effects = true,
}: SmoothScrollLayoutProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!wrapperRef.current || !contentRef.current) return;
    const refreshAll = () => {
      ScrollTrigger.clearScrollMemory();
      ScrollTrigger.sort();
      ScrollTrigger.refresh(true);
    };

    const mm = gsap.matchMedia();

    mm.add("(min-width: 768px) and (prefers-reduced-motion: no-preference)", () => {
      ScrollSmoother.get()?.kill();

      const smoother = ScrollSmoother.create({
        wrapper: wrapperRef.current,
        content: contentRef.current,
        smooth,
        effects,
      });

      smoother.scrollTo(0, false);
      const rafId = requestAnimationFrame(refreshAll);

      const handlePageShow = (event: PageTransitionEvent) => {
        if (event.persisted) {
          refreshAll();
        }
      };

      const handleVisibility = () => {
        if (document.visibilityState === "visible") {
          refreshAll();
        }
      };

      window.addEventListener("pageshow", handlePageShow);
      document.addEventListener("visibilitychange", handleVisibility);

      return () => {
        cancelAnimationFrame(rafId);
        window.removeEventListener("pageshow", handlePageShow);
        document.removeEventListener("visibilitychange", handleVisibility);
        smoother.kill();
      };
    });

    mm.add("(max-width: 767px), (prefers-reduced-motion: reduce)", () => {
      ScrollSmoother.get()?.kill();
      const rafId = requestAnimationFrame(refreshAll);
      return () => cancelAnimationFrame(rafId);
    });

    return () => mm.revert();
  }, [smooth, effects]);

  return (
    <div ref={wrapperRef} data-smooth-wrapper="true" className={wrapperClassName}>
      <div ref={contentRef} data-smooth-content="true" className={contentClassName}>
        {children}
      </div>
    </div>
  );
};

export default SmoothScrollLayout;
